// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal, Form } from "react-bootstrap";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeChangeAwsUser } from "actions";
import { DataState } from "types";
import { tabClickHandler } from "guiutils";
import { isSet } from "utils";
import { ReactComponent as IconUser } from "static/imgUser.svg";

import "./UserPwdChange.scss";

interface IUserPwdChangeExtProps {
  showInfoBox?: boolean;
}
interface IUserPwdChangeProps {
  secondaryId: string;
  realName: string | undefined;
  email: string | undefined;
  dataState: DataState;
  saveResult: string | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IUserPwdChangeProps = {
    secondaryId: state.site.user?.secId ?? "",
    realName: state.site.user?.realName,
    email: state.site.user?.email,
    dataState: state.scheduler.loginDataState,
    saveResult: state.gui.login.userStateErrorInfo,
  };
  return props;
};

interface IUserPwdChangeState {
  oldPassword: string;
  newPassword: string;
  newPassword2: string;
  realName: string;
  showMismatchWaring: boolean;
}

interface IUserPwdChangeDispatch {
  onChangePassword: (
    secId: string,
    oldPassword: string,
    newPassword: string,
    name: string | undefined,
  ) => void;
  onCancel: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserPwdChangeDispatch = {
    onChangePassword: (
      secId: string,
      oldPassword: string,
      newPassword: string,
      name: string | undefined,
    ) => {
      const payload: IActionTypeChangeAwsUser = {
        secondaryId: secId,
        oldPassword,
        newPassword,
        realName: name,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_AWSUSER_REQ, payload));
    },
    onCancel: () => {
      dispatch(raiseAction(ActionType.ACT_CANCEL_PWD_CHANGE));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class UserPwdChangeComponent extends React.Component<
  IUserPwdChangeProps & IUserPwdChangeExtProps & IUserPwdChangeDispatch & WrappedComponentProps,
  IUserPwdChangeState
> {
  state: IUserPwdChangeState = {
    oldPassword: "",
    newPassword: "",
    newPassword2: "",
    realName: "",
    showMismatchWaring: false,
  };

  public render() {
    return (
      <>
        {this.renderModal()}
        {this.renderUserForm()}
        {this.props.showInfoBox === true && this.props.dataState === DataState.SAVE_REQ && (
          <div className="alert alert-warning mt-2">
            <FormattedMessage id="Changing password..." />
          </div>
        )}
        {this.props.showInfoBox === true && this.props.dataState === DataState.SAVE_FAIL && (
          <div className="alert alert-danger mt-2">
            <FormattedMessage id="Changing password failed." />
            <br></br>
            {isSet(this.props.saveResult) && <FormattedMessage id={this.props.saveResult} />}
          </div>
        )}
      </>
    );
  }

  private renderUserForm() {
    const doDataCompletion = this.props.realName === undefined;
    return (
      <div id="user">
        <div id="user_inner">
          <IconUser width={50} />
          <div className="user_right">
            <h1>
              {doDataCompletion ? (
                <FormattedMessage id="Complete user data" />
              ) : (
                <FormattedMessage id="Modify current user" />
              )}
            </h1>
            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <table style={{ width: "100%" }}>
                <tbody>
                  <tr>
                    <td>
                      {doDataCompletion ? (
                        <FormattedMessage id="Full name" />
                      ) : (
                        <FormattedMessage id="Email" />
                      )}
                    </td>
                    <td>
                      {doDataCompletion ? (
                        <>
                          <Form.Control
                            type="text"
                            name="realName"
                            className={this.state.realName.length === 0 ? undefined : "input-error"}
                            value={this.state.realName}
                            autoFocus
                            disabled={this.props.dataState === DataState.SAVE_REQ}
                            onChange={(event) => {
                              this.setState({ realName: event?.currentTarget.value });
                            }}
                          />
                          {this.props.dataState === DataState.SAVE_FAIL &&
                            this.state.realName.length === 0 && (
                              <div className="loginError">
                                <FormattedMessage id="User name needed!" />
                              </div>
                            )}
                        </>
                      ) : (
                        <span style={{ fontWeight: "bolder" }}>{this.props.email}</span>
                      )}
                      <div className="mb-2"></div>
                    </td>
                  </tr>
                  {!doDataCompletion && (
                    <>
                      <tr className="loginLabel">
                        <td>
                          <FormattedMessage id="Old password" />:
                        </td>
                        <td>
                          <Form.Control
                            type="password"
                            name="oldPassword"
                            className={
                              this.props.dataState === DataState.SAVE_FAIL &&
                              this.state.oldPassword.length === 0
                                ? "input-error"
                                : undefined
                            }
                            value={this.state.oldPassword}
                            autoFocus
                            disabled={this.props.dataState === DataState.SAVE_REQ}
                            onChange={(event) => {
                              this.setState({ oldPassword: event?.currentTarget.value });
                            }}
                          />
                          {this.props.dataState === DataState.SAVE_FAIL &&
                            this.state.oldPassword.length === 0 && (
                              <div className="loginError">
                                <FormattedMessage id="Password needed" />
                              </div>
                            )}
                          <div className="mb-2"></div>
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td>
                      <FormattedMessage id="New password" />:
                    </td>
                    <td>
                      <>
                        <Form.Control
                          type="password"
                          name="newPassword"
                          className={
                            this.props.dataState !== DataState.SAVE_FAIL ||
                            (this.state.newPassword === this.state.newPassword2 &&
                              this.state.newPassword.length !== 0)
                              ? undefined
                              : "input-error"
                          }
                          value={this.state.newPassword}
                          autoFocus
                          disabled={this.props.dataState === DataState.SAVE_REQ}
                          onChange={(event) => {
                            this.setState({ newPassword: event?.currentTarget.value });
                          }}
                        />
                        {this.props.dataState === DataState.SAVE_FAIL && (
                          <div className="loginError">
                            {this.state.newPassword.length === 0 ? (
                              <FormattedMessage id="Password needed" />
                            ) : this.state.newPassword !== this.state.newPassword2 ? (
                              <FormattedMessage id="Passwords are different" />
                            ) : (
                              ""
                            )}
                          </div>
                        )}
                      </>
                      <div className="mb-2"></div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FormattedMessage id="Verify new password" />:
                    </td>
                    <td>
                      <Form.Control
                        type="password"
                        name="newPassword2"
                        className={
                          this.props.dataState !== DataState.SAVE_FAIL ||
                          (this.state.newPassword === this.state.newPassword2 &&
                            this.state.newPassword2.length !== 0)
                            ? undefined
                            : "input-error"
                        }
                        value={this.state.newPassword2}
                        autoFocus
                        disabled={this.props.dataState === DataState.SAVE_REQ}
                        onChange={(event) => {
                          this.setState({ newPassword2: event?.currentTarget.value });
                        }}
                      />
                      {this.props.dataState === DataState.SAVE_FAIL && (
                        <div className="loginError">
                          {this.state.newPassword2.length === 0 ? (
                            <FormattedMessage id="Password needed" />
                          ) : this.state.newPassword !== this.state.newPassword2 ? (
                            <FormattedMessage id="Passwords are different" />
                          ) : (
                            ""
                          )}
                        </div>
                      )}
                      <div className="mb-2"></div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-3 buttonbox">
                <Button
                  id="userpwdchange"
                  type="submit"
                  variant="primary"
                  onClick={(event) => this.changePassword()}
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                >
                  {doDataCompletion ? (
                    <FormattedMessage id="Finish User Setup" />
                  ) : (
                    <FormattedMessage id="Change Password" />
                  )}
                </Button>
                {!doDataCompletion && (
                  <Button variant="secondary" onClick={(event) => this.props.onCancel()}>
                    <FormattedMessage id="Cancel" />
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  private renderModal() {
    return (
      <Modal
        show={this.state.showMismatchWaring}
        onHide={() => this.setState({ showMismatchWaring: false })}
      >
        <Modal.Header closeButton>
          <FormattedMessage id="Changing User" />
        </Modal.Header>
        <Modal.Body>
          <FormattedMessage id="The two fields for the new password do not match." />{" "}
          <FormattedMessage id="Please enter the same password twice." />
        </Modal.Body>
      </Modal>
    );
  }

  private changePassword() {
    const doDataCompletion = this.props.realName === undefined;
    // check for matching new password
    if (this.state.newPassword === this.state.newPassword2) {
      this.props.onChangePassword(
        this.props.secondaryId,
        this.state.oldPassword,
        this.state.newPassword,
        doDataCompletion ? this.state.realName : undefined,
      );
    } else {
      this.setState({ showMismatchWaring: true });
    }
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(UserPwdChangeComponent));
